import { useAuth0 } from "@auth0/auth0-react";
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { X } from "react-feather";
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
} from "@mui/material";
import styles from "../vehiclerequests.module.css";
import { apiErrorMessage } from "../vehicleRequestUtils";

const SendLinkDialog = ({ onClose, onSent }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [propertyList, setPropertyList] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(true);
  const [propertyId, setPropertyId] = useState("");
  const [tenant, setTenant] = useState(null);
  const [channel, setChannel] = useState("sms");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    async function getProperties() {
      try {
        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
        const response = await axios(process.env.REACT_APP_TENANT_API_URL + "v1/property-list2", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPropertyList(response?.data?.data || []);
      } catch (err) {
        console.error("[VehicleRequests] Failed to load tenants for send link", err);
        setError(apiErrorMessage(err, "Unable to load tenants. Please close and try again."));
      } finally {
        setPropertiesLoading(false);
      }
    }
    getProperties();
  }, [getAccessTokenSilently]);

  const tenantOptions = useMemo(() => {
    const selected = propertyList.find((item) => item?.id === propertyId);
    return (selected?.tenants2 || [])
      .filter((item, index, self) => index === self.findIndex((t) => t.id === item.id))
      .map((item) => ({ id: item.id, name: `${item?.first_name || ""} ${item?.last_name || ""}`.trim(), apt_no: item?.apt_no }));
  }, [propertyList, propertyId]);

  const send = async () => {
    if (!tenant) {
      setError("Please select a tenant.");
      return;
    }

    setSending(true);
    setError("");
    try {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
      const response = await axios.post(
        process.env.REACT_APP_TENANT_API_URL + "v1/vehicle-requests/send-link",
        { tenant_info_id: tenant.id, channel },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onSent(response?.data?.message || "Vehicle update link sent.");
    } catch (err) {
      console.error("[VehicleRequests] Failed to send vehicle update link", err);
      setError(apiErrorMessage(err, "Unable to send the link. Please try again."));
    } finally {
      setSending(false);
    }
  };

  return (<Dialog open onClose={sending ? undefined : onClose} fullWidth maxWidth="sm" className="NewPopUp MobileForm">
    <DialogTitle>
      Send vehicle update link
      <button onClick={onClose} disabled={sending}><X /></button>
    </DialogTitle>
    <DialogContent className={styles.dialogBody}>
      <p className={styles.mutedText}>The tenant gets a private link to add or update their vehicles with photos and to invite a guest vehicle. Every change waits for approval here.</p>

      <FormControl fullWidth className={styles.formField}>
        <InputLabel id="send-link-property">Property*</InputLabel>
        <Select labelId="send-link-property" label="Property*" value={propertyId} disabled={propertiesLoading} onChange={(e) => { setPropertyId(e.target.value); setTenant(null); }}>
          {propertyList.map((item) => (<MenuItem key={item.id} value={item.id} disabled={(item?.tenants2 || []).length === 0}>{item.name}</MenuItem>))}
        </Select>
      </FormControl>

      <Autocomplete
        className={styles.formField}
        options={tenantOptions}
        value={tenant}
        onChange={(e, selected) => setTenant(selected)}
        getOptionLabel={(option) => option?.name || ""}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        noOptionsText={propertyId === "" ? "Select a property first" : "No tenants found"}
        renderOption={(props, option) => (<li {...props} key={option.id}>
          <div className={styles.optionText}>
            <span>{option.name}</span>
            {option.apt_no && <small>{option.apt_no}</small>}
          </div>
        </li>)}
        renderInput={(params) => (<TextField {...params} label="Tenant*" placeholder="Search tenant..." />)}
      />

      <RadioGroup row value={channel} onChange={(e) => setChannel(e.target.value)} className={styles.formField}>
        <FormControlLabel value="sms" control={<Radio />} label="Text message" />
        <FormControlLabel value="email" control={<Radio />} label="Email" />
        <FormControlLabel value="both" control={<Radio />} label="Both" />
      </RadioGroup>

      {error !== "" && (<Alert severity="error" className={styles.inlineAlert}>{error}</Alert>)}
    </DialogContent>
    <DialogActions>
      <Button className="CancelPopupBU" disabled={sending} onClick={onClose}>Cancel</Button>
      <Button className="SubmitPopupBU" disabled={sending || propertiesLoading} onClick={send}>{sending ? "Sending..." : "Send link"}</Button>
    </DialogActions>
  </Dialog>);
};

export default SendLinkDialog;
